import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  Shield, Bell, BellRing, AlertTriangle, Check, X, Ban, Eye, 
  MessageSquare, Star, Clock, User, Flag, ChevronDown, ChevronRight,
  Trophy, Medal, Award, Users, TrendingUp, Filter, MapPin, FileText, Building2, Lock, LogOut
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Policy rule icons and colors
const RULE_STYLES = {
  no_unsolicited_offerings: { icon: "🚫", color: "bg-orange-100 text-orange-800 border-orange-200" },
  no_offensive_content: { icon: "⚠️", color: "bg-red-100 text-red-800 border-red-200" },
  appropriate_content: { icon: "👁️", color: "bg-purple-100 text-purple-800 border-purple-200" },
  no_spam: { icon: "🔄", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  no_personal_attacks: { icon: "👤", color: "bg-pink-100 text-pink-800 border-pink-200" },
  no_false_information: { icon: "❌", color: "bg-gray-100 text-gray-800 border-gray-200" },
  no_self_promotion: { icon: "📢", color: "bg-blue-100 text-blue-800 border-blue-200" },
  respectful_language: { icon: "💬", color: "bg-green-100 text-green-800 border-green-200" }
};

export default function ModerationPage() {
  const navigate = useNavigate();
  
  // Admin authentication state - shares session with AdminPage
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [policyRules, setPolicyRules] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [pendingSubmissionReviews, setPendingSubmissionReviews] = useState([]);
  const [venueSubmissions, setVenueSubmissions] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [selectedViolations, setSelectedViolations] = useState([]);
  const [moderatorNotes, setModeratorNotes] = useState("");
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banTarget, setBanTarget] = useState(null);
  const [banForm, setBanForm] = useState({ reason: "", permanent: false, ban_days: 30 });
  const [leaderboardPeriod, setLeaderboardPeriod] = useState("all_time");

  // Check if already authenticated (shared admin session)
  useEffect(() => {
    const adminAuth = sessionStorage.getItem("strider_admin_auth");
    if (adminAuth === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  // Handle admin login
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/verify`, { password });
      if (response.data.valid) {
        setIsAuthenticated(true);
        sessionStorage.setItem("strider_admin_auth", "true");
        setLoginError("");
      } else {
        setLoginError("Invalid password");
      }
    } catch (error) {
      setLoginError("Invalid password");
    }
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem("strider_admin_auth");
    navigate("/admin");
  };

  const loadDashboard = useCallback(async () => {
    try {
      const [dashRes, rulesRes, leaderRes, pendingReviewsRes, submissionsRes] = await Promise.all([
        axios.get(`${API}/moderation/dashboard`),
        axios.get(`${API}/moderation/policy-rules`),
        axios.get(`${API}/contributors/leaderboard?period=${leaderboardPeriod}&limit=10`),
        axios.get(`${API}/moderation/pending-reviews`).catch(() => ({ data: [] })),
        axios.get(`${API}/locations/pending`).catch(() => ({ data: [] }))
      ]);
      setDashboard(dashRes.data);
      setPolicyRules(rulesRes.data);
      setLeaderboard(leaderRes.data);
      setPendingSubmissionReviews(pendingReviewsRes.data || []);
      const subs = submissionsRes.data || [];
      setVenueSubmissions(Array.isArray(subs) ? subs : []);
      
      // Show notification if there are pending items
      const totalPending = (dashRes.data?.stats?.pending || 0) + 
                          (pendingReviewsRes.data?.length || 0) + 
                          (Array.isArray(subs) ? subs.length : 0);
      if (totalPending > 0) {
        document.title = `(${totalPending}) Content Moderation`;
      } else {
        document.title = "Content Moderation";
      }
    } catch (error) {
      toast.error("Failed to load moderation dashboard");
    } finally {
      setLoading(false);
    }
  }, [leaderboardPeriod]);

  useEffect(() => {
    if (isAuthenticated) {
      loadDashboard();
      // Poll for new reviews every 30 seconds
      const interval = setInterval(loadDashboard, 30000);
      return () => clearInterval(interval);
    }
  }, [loadDashboard, isAuthenticated]);

  const approveReview = async (reviewId) => {
    try {
      await axios.post(`${API}/moderation/reviews/${reviewId}/approve`);
      toast.success("Review approved!");
      loadDashboard();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to approve review");
    }
  };

  const openRemoveModal = (review) => {
    setSelectedReview(review);
    setSelectedViolations([]);
    setModeratorNotes("");
    setShowRemoveModal(true);
  };

  const removeReview = async () => {
    if (selectedViolations.length === 0) {
      toast.error("Please select at least one policy violation");
      return;
    }

    try {
      await axios.post(`${API}/moderation/reviews/${selectedReview.id}/remove`, {
        violation_rules: selectedViolations,
        moderator_notes: moderatorNotes,
        notify_user: true
      });
      toast.success("Review removed and user notified");
      setShowRemoveModal(false);
      loadDashboard();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to remove review");
    }
  };

  const openBanModal = (contributor) => {
    setBanTarget(contributor);
    setBanForm({ reason: "", permanent: false, ban_days: 30 });
    setShowBanModal(true);
  };

  const banContributor = async () => {
    if (!banForm.reason.trim()) {
      toast.error("Please provide a reason for the ban");
      return;
    }

    try {
      await axios.post(`${API}/contributors/${banTarget.contributor_id || banTarget.id}/ban`, banForm);
      toast.success(`Contributor ${banForm.permanent ? "permanently" : "temporarily"} banned`);
      setShowBanModal(false);
      loadDashboard();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to ban contributor");
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${API}/moderation/notifications/${notificationId}/read`);
      loadDashboard();
    } catch (error) {
      console.error("Failed to mark notification read:", error);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await axios.post(`${API}/moderation/notifications/mark-all-read`);
      toast.success("All notifications marked as read");
      loadDashboard();
    } catch (error) {
      toast.error("Failed to mark notifications read");
    }
  };

  // Login Screen - requires admin authentication
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-brand" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Content Moderation</h1>
            <p className="text-gray-500 mt-2">Admin access required</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Admin Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand" data-testid="moderation-password-input" />
              </div>
              {loginError && <p className="mt-2 text-sm text-red-600 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {loginError}</p>}
            </div>
            <button type="submit" className="w-full bg-brand text-white py-3 rounded-lg hover:bg-brand-dark transition-colors font-medium" data-testid="moderation-login-btn">Access Moderation Panel</button>
          </form>
          <button onClick={() => navigate("/admin")} className="w-full mt-4 text-gray-500 hover:text-gray-700 text-sm">← Back to Admin</button>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand"></div>
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Shield className="w-7 h-7 md:w-8 md:h-8 text-brand" />
            <div>
              <h1 className="text-lg md:text-xl font-bold text-gray-900">Content Moderation</h1>
              <p className="text-xs md:text-sm text-gray-500 hidden sm:block">Review management & contributor oversight</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/admin")}
              className="btn-mobile bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Back to Admin
            </button>
            {dashboard?.notifications?.length > 0 && (
              <div className="relative p-2">
                <BellRing className="w-6 h-6 text-amber-500 animate-pulse" />
                <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {dashboard.notifications.length}
                </span>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm"
              data-testid="moderation-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Overview - Enhanced */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
          <StatCard 
            icon={<MessageSquare className="w-5 h-5" />}
            label="Total Reviews"
            value={stats.total_reviews || 0}
            color="blue"
          />
          <StatCard 
            icon={<Clock className="w-5 h-5" />}
            label="Pending Reviews"
            value={stats.pending || 0}
            color="amber"
            highlight={stats.pending > 0}
          />
          <StatCard 
            icon={<FileText className="w-5 h-5" />}
            label="Submission Reviews"
            value={pendingSubmissionReviews.length || 0}
            color="orange"
            highlight={pendingSubmissionReviews.length > 0}
          />
          <StatCard 
            icon={<Building2 className="w-5 h-5" />}
            label="Venue Submissions"
            value={venueSubmissions.length || 0}
            color="indigo"
            highlight={venueSubmissions.length > 0}
          />
          <StatCard 
            icon={<Check className="w-5 h-5" />}
            label="Approved"
            value={stats.approved || 0}
            color="green"
          />
          <StatCard 
            icon={<X className="w-5 h-5" />}
            label="Removed"
            value={stats.removed || 0}
            color="red"
          />
          <StatCard 
            icon={<Users className="w-5 h-5" />}
            label="Contributors"
            value={stats.total_contributors || 0}
            color="purple"
          />
          <StatCard 
            icon={<Ban className="w-5 h-5" />}
            label="Banned"
            value={stats.banned_contributors || 0}
            color="gray"
          />
        </div>
        
        {/* Quick Action Alerts - Show when items need attention */}
        {(pendingSubmissionReviews.length > 0 || venueSubmissions.length > 0 || stats.pending > 0) && (
          <div className="mb-6 space-y-3">
            {pendingSubmissionReviews.length > 0 && (
              <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <FileText className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-orange-800">
                      {pendingSubmissionReviews.length} review{pendingSubmissionReviews.length > 1 ? 's' : ''} from venue submissions
                    </p>
                    <p className="text-sm text-orange-600">Reviews submitted with new venue additions</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    // Scroll to the pending section below
                    const pendingSection = document.querySelector('[data-state="active"]');
                    if (pendingSection) {
                      pendingSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                >
                  View Below
                </button>
              </div>
            )}
            {venueSubmissions.length > 0 && (
              <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-indigo-800">
                      {venueSubmissions.length} new venue submission{venueSubmissions.length > 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-indigo-600">Waiting for approval</p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    // Scroll to the pending tab content instead of navigating away
                    const pendingSection = document.querySelector('[data-state="active"]');
                    if (pendingSection) {
                      pendingSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                >
                  View Below
                </button>
              </div>
            )}
          </div>
        )}

        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="bg-white rounded-lg p-1 border border-gray-200">
            <TabsTrigger value="pending" className="relative">
              Pending Reviews
              {stats.pending > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                  {stats.pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="notifications">
              Notifications
              {dashboard?.notifications?.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                  {dashboard.notifications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="violations">Recent Violations</TabsTrigger>
            <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
            <TabsTrigger value="policy">Policy Rules</TabsTrigger>
          </TabsList>

          {/* Pending Reviews Tab - Now includes pending venue submissions */}
          <TabsContent value="pending">
            <div className="space-y-6">
              {/* Pending Venue Submissions Section */}
              {venueSubmissions.length > 0 && (
                <div className="bg-white rounded-xl border border-indigo-200 overflow-hidden">
                  <div className="p-4 bg-indigo-50 border-b border-indigo-200">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-medium text-indigo-800">
                        Pending Venue Submissions ({venueSubmissions.length})
                      </h3>
                    </div>
                    <p className="text-sm text-indigo-600 mt-1">New venues waiting for approval</p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {venueSubmissions.map((submission) => (
                      <VenueSubmissionCard 
                        key={submission.id} 
                        submission={submission}
                        onReview={() => navigate('/admin?tab=submissions')}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Pending Reviews Section */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-gray-600" />
                    <h3 className="font-medium text-gray-800">
                      Pending User Reviews ({dashboard?.pending_reviews?.length || 0})
                    </h3>
                  </div>
                </div>
                {dashboard?.pending_reviews?.length === 0 && venueSubmissions.length === 0 ? (
                  <div className="p-12 text-center">
                    <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">All caught up!</h3>
                    <p className="text-gray-500">No pending items to moderate</p>
                  </div>
                ) : dashboard?.pending_reviews?.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Check className="w-8 h-8 mx-auto mb-2 text-green-400" />
                    No pending user reviews
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {dashboard?.pending_reviews?.map((review) => (
                      <ReviewCard 
                        key={review.id}
                        review={review}
                        onApprove={() => approveReview(review.id)}
                        onRemove={() => openRemoveModal(review)}
                        onBanUser={() => openBanModal(review)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-medium text-gray-900">New Review Notifications</h3>
                {dashboard?.notifications?.length > 0 && (
                  <button
                    onClick={markAllNotificationsRead}
                    className="text-sm text-brand hover:underline"
                  >
                    Mark all as read
                  </button>
                )}
              </div>
              {dashboard?.notifications?.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No unread notifications
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {dashboard?.notifications?.map((notif) => (
                    <div 
                      key={notif.id}
                      className="p-4 hover:bg-gray-50 flex items-start gap-4 cursor-pointer"
                      onClick={() => markNotificationRead(notif.id)}
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          New review from {notif.contributor_name}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{notif.preview}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notif.created_at).toLocaleString()}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Recent Violations Tab */}
          <TabsContent value="violations">
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-medium text-gray-900">Recently Removed Reviews</h3>
              </div>
              {dashboard?.recent_violations?.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Shield className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No recent violations
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {dashboard?.recent_violations?.map((review) => (
                    <div key={review.id} className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <Flag className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{review.user_name}</span>
                            <span className="text-xs text-gray-400">
                              {new Date(review.removed_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{review.comment?.substring(0, 150)}...</p>
                          <div className="flex flex-wrap gap-1">
                            {review.violation_rules?.map((rule) => (
                              <span 
                                key={rule}
                                className={`px-2 py-0.5 text-xs rounded-full border ${RULE_STYLES[rule]?.color || 'bg-gray-100'}`}
                              >
                                {RULE_STYLES[rule]?.icon} {policyRules[rule]?.title || rule}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Top Contributors
                </h3>
                <select
                  value={leaderboardPeriod}
                  onChange={(e) => setLeaderboardPeriod(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-3 py-1.5"
                >
                  <option value="all_time">All Time</option>
                  <option value="monthly">This Month</option>
                  <option value="yearly">This Year</option>
                </select>
              </div>
              <div className="divide-y divide-gray-100">
                {leaderboard.map((contributor, index) => (
                  <div 
                    key={contributor.id}
                    className={`p-4 flex items-center gap-4 ${index < 3 ? 'bg-gradient-to-r from-amber-50/50 to-transparent' : ''}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-amber-400 text-white' :
                      index === 1 ? 'bg-gray-300 text-white' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {index < 3 ? ['🥇', '🥈', '🥉'][index] : index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{contributor.name}</span>
                        <span className="text-lg" title={contributor.level_name}>
                          {contributor.level_badge}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>{contributor.xp?.toLocaleString()} XP</span>
                        <span>Level {contributor.level}</span>
                        <span>{contributor.badge_count} badges</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-brand">
                        {contributor.stats?.total_reviews || 0}
                      </div>
                      <div className="text-xs text-gray-500">reviews</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Policy Rules Tab */}
          <TabsContent value="policy">
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-medium text-gray-900">Content Policy Rules</h3>
                <p className="text-sm text-gray-500">These rules are enforced for all user-generated content</p>
              </div>
              <div className="divide-y divide-gray-100">
                {Object.entries(policyRules).map(([ruleId, rule]) => (
                  <div key={ruleId} className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{RULE_STYLES[ruleId]?.icon || '📋'}</span>
                      <div>
                        <h4 className="font-medium text-gray-900">{rule.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{rule.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Remove Review Modal */}
      {showRemoveModal && selectedReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Remove Review</h3>
              <p className="text-sm text-gray-500">Select the policy violations and add notes</p>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Review Preview */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-700">{selectedReview.user_name}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedReview.comment}</p>
              </div>

              {/* Policy Violations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Policy Violations *
                </label>
                <div className="space-y-2">
                  {Object.entries(policyRules).map(([ruleId, rule]) => (
                    <label 
                      key={ruleId}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedViolations.includes(ruleId) 
                          ? 'border-red-300 bg-red-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedViolations.includes(ruleId)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedViolations([...selectedViolations, ruleId]);
                          } else {
                            setSelectedViolations(selectedViolations.filter(r => r !== ruleId));
                          }
                        }}
                        className="mt-1 rounded border-gray-300"
                      />
                      <div>
                        <span className="font-medium text-gray-900 text-sm">
                          {RULE_STYLES[ruleId]?.icon} {rule.title}
                        </span>
                        <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Moderator Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Moderator Notes (optional)
                </label>
                <textarea
                  value={moderatorNotes}
                  onChange={(e) => setModeratorNotes(e.target.value)}
                  placeholder="Additional context for the user..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                  rows={3}
                />
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowRemoveModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={removeReview}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Remove & Notify User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ban User Modal */}
      {showBanModal && banTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-500" />
                Ban Contributor
              </h3>
              <p className="text-sm text-gray-500">
                Banning: {banTarget.user_name || banTarget.name}
              </p>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Ban *
                </label>
                <textarea
                  value={banForm.reason}
                  onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })}
                  placeholder="Explain why this user is being banned..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none"
                  rows={3}
                />
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={banForm.permanent}
                    onChange={(e) => setBanForm({ ...banForm, permanent: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Permanent ban</span>
                </label>
              </div>

              {!banForm.permanent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ban Duration (days)
                  </label>
                  <input
                    type="number"
                    value={banForm.ban_days}
                    onChange={(e) => setBanForm({ ...banForm, ban_days: parseInt(e.target.value) || 30 })}
                    min={1}
                    max={365}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowBanModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={banContributor}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Ban User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ icon, label, value, color, highlight }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    green: "bg-green-50 text-green-600 border-green-100",
    red: "bg-red-50 text-red-600 border-red-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    gray: "bg-gray-50 text-gray-600 border-gray-100",
    orange: "bg-orange-50 text-orange-600 border-orange-100",
    indigo: "bg-indigo-50 text-indigo-600 border-indigo-100",
    teal: "bg-teal-50 text-teal-600 border-teal-100"
  };

  return (
    <div className={`p-4 rounded-xl border ${highlight ? 'ring-2 ring-amber-400 ring-offset-2 animate-pulse' : ''} ${colors[color] || colors.gray}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs font-medium opacity-80 leading-tight break-words">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value.toLocaleString()}</div>
    </div>
  );
}

// Review Card Component
function ReviewCard({ review, onApprove, onRemove, onBanUser }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="p-4 hover:bg-gray-50">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-gray-500" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900">{review.user_name}</span>
            {review.venue_name && (
              <>
                <span className="text-gray-400">on</span>
                <span className="text-brand font-medium">{review.venue_name}</span>
              </>
            )}
          </div>
          
          <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
            <span className="flex items-center gap-1">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              {review.rating}/10
            </span>
            <span>{new Date(review.created_at).toLocaleString()}</span>
          </div>
          
          <p className={`text-sm text-gray-700 ${expanded ? '' : 'line-clamp-2'}`}>
            {review.comment}
          </p>
          
          {review.comment?.length > 150 && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-brand mt-1 hover:underline"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onApprove}
            className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200 transition-colors"
            title="Approve"
            data-testid="approve-review-btn"
          >
            <Check className="w-5 h-5" />
          </button>
          <button
            onClick={onRemove}
            className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
            title="Remove"
            data-testid="remove-review-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={onBanUser}
            className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            title="Ban User"
            data-testid="ban-user-btn"
          >
            <Ban className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Venue Submission Card Component - for pending venue approvals
function VenueSubmissionCard({ submission, onReview }) {
  return (
    <div className="p-4 hover:bg-indigo-50/50">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-6 h-6 text-indigo-600" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-medium text-gray-900">{submission.name}</span>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Pending</span>
          </div>
          
          <div className="flex items-center gap-3 text-sm text-gray-500 mb-2 flex-wrap">
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4 text-gray-400" />
              {submission.location?.city || submission.city || 'Unknown location'}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-gray-400" />
              {new Date(submission.submitted_at || submission.created_at).toLocaleDateString()}
            </span>
          </div>
          
          {submission.description && (
            <p className="text-sm text-gray-600 line-clamp-2">
              {submission.description}
            </p>
          )}
          
          <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
            <span>Submitted by: {submission.submitted_by || submission.contributor_email || 'Anonymous'}</span>
          </div>
        </div>

        <button
          onClick={onReview}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium flex-shrink-0"
          data-testid={`review-submission-${submission.id}`}
        >
          Review
        </button>
      </div>
    </div>
  );
}
